import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import SubscriptionPlans from '@/components/page-components/project/agents/SubscriptionPlans';
import { SubscriptionUsageCard } from '@/components/page-components/billing/SubscriptionUsageCard';
import type { Agent } from '@/types/Agents';
import {
  getBillingCycleDisplay,
  getSubscriptionStatusMeta,
  getUsageMetrics,
  isActiveSubscription,
  canRequestSubscription,
} from '@/utils/subscriptionUtils';
import { getIntlLocale } from '@/utils/supportedLanguages';

interface AgentSubscriptionCardProps {
  agent: Agent;
  onRefresh: () => void | Promise<void>;
  onCancelRequest: (agentId: number) => Promise<void>;
  isCancelling?: boolean;
  defaultExpandedPlans?: boolean;
}

export const AgentSubscriptionCard: React.FC<AgentSubscriptionCardProps> = ({
  agent,
  onRefresh,
  onCancelRequest,
  isCancelling = false,
  defaultExpandedPlans = false,
}) => {
  const { t, i18n } = useTranslation();
  const [plansOpen, setPlansOpen] = useState(defaultExpandedPlans);

  const subscription = agent.subscription;
  const status = subscription?.status;
  const statusMeta = getSubscriptionStatusMeta(status);
  const cycle = getBillingCycleDisplay(
    subscription?.activated_at,
    subscription?.expiring_at,
    status
  );
  const { hasUsage } = getUsageMetrics(subscription);
  const agentId = agent.id!;
  const formatMoney = (amount: number) =>
    new Intl.NumberFormat(getIntlLocale(i18n.resolvedLanguage), {
      style: 'currency',
      currency: subscription?.currency ?? 'EUR',
    }).format(amount);
  const billingPeriod = subscription?.billing_period ?? 'monthly';
  const billingPeriodLabel = t(
    `subscription.plans.period.${billingPeriod}`,
    billingPeriod
  );

  const renderBillingCycle = () => {
    switch (cycle.type) {
      case 'requested':
        return cycle.date
          ? t(
              'billing.cycle.requested_on.translation',
              'Requested on {{date}}',
              {
                date: cycle.date,
              }
            )
          : t(
              'billing.cycle.request_pending.translation',
              'Request pending review'
            );
      case 'range':
        return t('billing.cycle.range.translation', '{{start}} – {{end}}', {
          start: cycle.start,
          end: cycle.end,
        });
      case 'started':
        return t('billing.cycle.started.translation', 'Started {{date}}', {
          date: cycle.date,
        });
      case 'renews':
        return t('billing.cycle.renews.translation', 'Renews {{date}}', {
          date: cycle.date,
        });
      default:
        return t('billing.cycle.not_set.translation', 'Renewal date not set');
    }
  };

  const showCycleSection =
    status === 'active' ||
    status === 'requested' ||
    Boolean(subscription?.plan_name);

  return (
    <Card className="bg-card">
      <CardHeader className="gap-3 pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1 space-y-1 pr-2">
            <CardTitle>
              <Link
                to={`/agents/${agentId}?tab=usage`}
                className="hover:text-brand transition-colors"
              >
                {agent.name}
              </Link>
            </CardTitle>
            {subscription?.plan_name && (
              <CardDescription className="text-base">
                {subscription.plan_name}
                {subscription.price != null && (
                  <span className="ml-2 font-medium text-foreground">
                    {formatMoney(subscription.price)}
                    <span className="text-sm font-normal text-muted-foreground">
                      /{billingPeriodLabel}
                    </span>
                  </span>
                )}
              </CardDescription>
            )}
          </div>
          <Badge
            variant={statusMeta.badgeVariant}
            className="shrink-0 self-start"
          >
            {t(statusMeta.labelKey, statusMeta.defaultLabel)}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {showCycleSection && (
          <div className="text-sm">
            <p className="text-muted-foreground">
              {status === 'requested'
                ? t('billing.cycle.label.request.translation', 'Request date')
                : t('billing.cycle.label.period.translation', 'Billing cycle')}
            </p>
            <p className="font-medium text-foreground">
              {renderBillingCycle()}
            </p>
          </div>
        )}

        {hasUsage && isActiveSubscription(status) && (
          <SubscriptionUsageCard
            subscription={subscription}
            wrapped={false}
            showPlanName={false}
          />
        )}

        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
          {isActiveSubscription(status) && (
            <Button variant="outline" className="w-full sm:w-auto" asChild>
              <Link to={`/agents/${agentId}?tab=usage`}>
                {t('billing.actions.view_usage.translation', 'View usage')}
              </Link>
            </Button>
          )}

          {status === 'requested' && (
            <Button
              variant="outline"
              className="w-full sm:w-auto"
              disabled={isCancelling}
              onClick={() => onCancelRequest(agentId)}
            >
              {isCancelling ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('subscription.requested.cancelling', 'Cancelling...')}
                </>
              ) : (
                t(
                  'subscription.requested.cancel',
                  'Cancel Subscription Request'
                )
              )}
            </Button>
          )}

          {canRequestSubscription(status) && (
            <Collapsible open={plansOpen} onOpenChange={setPlansOpen}>
              <CollapsibleTrigger asChild>
                <Button variant="brand" className="w-full sm:w-auto">
                  {plansOpen
                    ? t('billing.actions.hide_plans.translation', 'Hide plans')
                    : t(
                        'billing.actions.request_plan.translation',
                        'Request plan'
                      )}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent open={plansOpen}>
                <div className="pt-4">
                  <SubscriptionPlans
                    agentId={agentId}
                    agentName={agent.name}
                    onPlanSelect={async () => {
                      setPlansOpen(false);
                      await onRefresh();
                    }}
                  />
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
